import React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { ThemedButton } from '@helpcenter/themed-button';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';
import { AnalyticsContext, UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ExperienceSuccess } from '@atlassian/ufo';
import { externalLinksProductExperience } from '../experiences';
import { ExternalLinkItem } from '../external-link-item';
import messages from './messages';
import type { ExternalLinksReponse } from 'rest/external-links/types';

export interface Props {
	links: ExternalLinksReponse['links'];
	total: number;
	onShowMoreToggle: () => void;
}

export const EXTERNAL_LINKS_TO_BE_SHOWN = 3;

export const ExternalLinksStateless: React.FC<Props & WrappedComponentProps> = (props) => {
	di(useIsPageInEditMode);
	const [isPageInEditMode] = useIsPageInEditMode();
	const {
		links,
		total,
		onShowMoreToggle,
		intl: { formatMessage },
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	React.useEffect(() => {
		if (total > 0) {
			const event = createAnalyticsEvent({
				analyticsType: UI_EVENT_TYPE,
				action: 'viewed',
				links_count: total,
			});
			event.fire();
		}
	}, [createAnalyticsEvent, total]);

	if (links.length < 1) {
		return null;
	}

	const doesMoreLinkExists = total > links.length;

	return (
		<AnalyticsContext attributes={{ links_count: total }}>
			<ExternalLinksWrapper>
				<Heading size="small" as="h2">
					<LabelWithIcon>
						<LinkIcon spacing="spacious" label="" />
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<span>{formatMessage(messages.externalLinksSectionHeader)}</span>
					</LabelWithIcon>
				</Heading>
				<ul>
					{links.map((link, index) => (
						<ExternalLinkItem
							{...link}
							key={link.id}
							position={index + 1}
							isPageInEditMode={isPageInEditMode}
						/>
					))}
				</ul>
				{total > EXTERNAL_LINKS_TO_BE_SHOWN && (
					<ShowMoreButton
						appearance="link"
						actionSubjectId={
							doesMoreLinkExists ? 'show-more-external-resources' : 'show-less-external-resources'
						}
						onClick={onShowMoreToggle}
					>
						{doesMoreLinkExists
							? formatMessage(messages.externalLinksSectionShowMore)
							: formatMessage(messages.externalLinksSectionShowLess)}
					</ShowMoreButton>
				)}
				<ExperienceSuccess experience={externalLinksProductExperience} />
			</ExternalLinksWrapper>
		</AnalyticsContext>
	);
};

export default injectIntl(
	withAnalyticsContext({
		component: 'externalResources',
	})(ExternalLinksStateless),
);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ExternalLinksWrapper = styled.div`
	ul {
		padding-left: ${token('space.0', '0px')};
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LabelWithIcon = styled.label`
	display: flex;
	align-items: center;

	> :not(:last-child) {
		margin-right: ${token('space.100', '8px')};
	}
`;

//@ts-ignore TypeScript upgrade 5.1.6, please fix this violation.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ShowMoreButton = styled(ThemedButton)`
	margin-top: ${token('space.100', '8px')};
	&:hover {
		text-decoration: none;
	}
`;
